import { useMutation } from '@tanstack/react-query';
import type {
  BankAccountDto,
  BankIngestCommitFields,
  BankIngestCommitResult,
  BankIngestPreviewFields,
  BankIngestPreviewResult,
  CreateBankAccountRequest,
  ManualBankEntryRequest,
  ManualBankEntryResult,
} from '@signalvote/shared';
import { bankIngestCommitRequest, bankIngestPreviewRequest } from '@signalvote/shared';
import { campaignHeaders, defineQuery, defineWrite } from '@/lib/data';
import { useRefreshFinanceAfter } from './useFinanceSettings';
import { apiJson, apiResponse } from './apiFetch';

/**
 * PPR-004: bank-ingestion hooks.
 *
 *   * <code>useBankAccounts</code> — populates the pre-upload account picker
 *     and the manual-entry account dropdown.
 *   * <code>usePreviewBankMapping</code> — drives the 5-row preview in the
 *     column-mapping confirmation UI.
 *   * <code>useCommitBankIngest</code> — finalize an ImportBatch.
 *   * <code>useIngestManualBankEntry</code> — manual escape hatch.
 */
const bankAccountsRead = defineQuery({
  key: () => ['finance', 'bank-ingestion', 'accounts'],
  reads: 'GET /api/finance/bank-ingestion/accounts',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<BankAccountDto[]>('/api/finance/bank-ingestion/accounts', {
      signal,
      headers: campaignHeaders(campaignId),
      error: (err) => new Error(err.error || 'Failed to load bank accounts'),
    }),
});

export function useBankAccounts() {
  return bankAccountsRead(undefined);
}

/**
 * Create a bank account inline from the import flow ("pick or create"), so a
 * treasurer with a brand-new statement isn't bounced to the Setup tab. The
 * display name is the only field — the column-mapping profile is learned on the
 * first mapped import (the existing `createdCustom` flow), not here.
 */
export const useCreateBankAccount = defineWrite({
  send: async (request: CreateBankAccountRequest) => {
    const response = await apiResponse('/api/finance/bank-ingestion/accounts', {
      method: 'POST',
      json: request,
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new BankIngestErrorWithCode(body.error || 'Failed to create bank account', body.code);
    }
    return body as BankAccountDto;
  },
});

export function usePreviewBankMapping() {
  return useMutation({
    mutationFn: async (request: BankIngestPreviewFields) => {
      const response = await apiResponse('/api/finance/bank-ingestion/preview', {
        method: 'POST',
        json: bankIngestPreviewRequest(request),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new BankIngestErrorWithCode(body.error || 'Failed to preview mapping', body.code);
      }
      return body as BankIngestPreviewResult;
    },
  });
}

const useCommitBankIngestWrite = defineWrite({
  send: async (request: BankIngestCommitFields) => {
    const response = await apiResponse('/api/finance/bank-ingestion/commit', {
      method: 'POST',
      json: bankIngestCommitRequest(request),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new BankIngestErrorWithCode(body.error || 'Failed to commit ingest', body.code);
    }
    return body as BankIngestCommitResult;
  },
});

export function useCommitBankIngest() {
  return useRefreshFinanceAfter(useCommitBankIngestWrite());
}

const useIngestManualBankEntryWrite = defineWrite({
  send: async (request: ManualBankEntryRequest) => {
    const response = await apiResponse('/api/finance/bank-ingestion/manual', {
      method: 'POST',
      json: request,
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new BankIngestErrorWithCode(body.error || 'Failed to add manual entry', body.code);
    }
    return body as ManualBankEntryResult;
  },
});

export function useIngestManualBankEntry() {
  return useRefreshFinanceAfter(useIngestManualBankEntryWrite());
}

/**
 * Backend error code (from `BankIngestionErrorCodes`)
 * attached to thrown errors so callers can switch on `code` instead of
 * parsing the message. Mirrors the PPR-003 `DetectorErrorWithCode` pattern.
 */
export class BankIngestErrorWithCode extends Error {
  public readonly code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}
